import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../lib/api';
import { isPushSupported, registerServiceWorker } from '../lib/push';

const POLL_MS = 30000;

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const { data } = await getNotifications();
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, POLL_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  // Listen to SW broadcast for immediate refresh when push arrives
  useEffect(() => {
    if (!isPushSupported()) return undefined;
    registerServiceWorker();
    const onMessage = (event) => {
      if (event?.data?.type === 'push-received') {
        toast.success(event.data.payload?.title || 'Nueva notificación', {
          description: event.data.payload?.body || '',
        });
        fetchData();
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, [fetchData]);

  const togglePanel = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      await fetchData();
      setLoading(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleOpenItem = async (notif) => {
    if (!notif.read) {
      try {
        await markNotificationRead(notif.id);
        setUnread((u) => Math.max(0, u - 1));
        setItems((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
      } catch (e) {}
    }
    setOpen(false);
    if (notif.type === 'appointment_new') navigate('/agenda');
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    try {
      await markAllNotificationsRead();
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success('Todas marcadas como leídas');
    } catch (err) {
      toast.error('No se pudieron marcar como leídas');
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={togglePanel}
        className="relative p-2 rounded-lg hover:bg-slate-700/60 text-slate-200 transition-colors"
        aria-label="Notificaciones"
        data-testid="notifications-bell"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notifications-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-h-[70vh] bg-white text-slate-900 rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 flex flex-col"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <span className="font-semibold">Notificaciones</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Marcar todas
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-6 flex items-center justify-center text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                No hay notificaciones por ahora
              </div>
            ) : (
              items.map((n) => (
                <button
                  type="button"
                  key={n.id}
                  onClick={() => handleOpenItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors flex gap-3 ${
                    n.read ? 'opacity-70' : ''
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full mt-2 shrink-0 ${
                      n.read ? 'bg-slate-300' : 'bg-blue-500'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-slate-900">{n.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-slate-400 mt-1">
                      {new Date(n.created_at).toLocaleString('es-MX', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
